<div class="w-full h-full px-5 py-3 flex flex-col justify-between gap-5">
  @switch (uploadingStatus) {
    @case (UploadingStatus.Successful) {
      <ng-container *ngTemplateOutlet="Successful"></ng-container>
    }
    @case (UploadingStatus.Failed) {
      <ng-container *ngTemplateOutlet="Failed"></ng-container>
    }
    @default {
      <ng-container *ngTemplateOutlet="Uploading"></ng-container>
    }
  }
</div>

<ng-template #Successful>
  <div class="flex justify-end items-center">
    <button
      mat-icon-button
      class="float-right -mt-3 -mr-3"
      (click)="resetStatus(); dialogRef.close()"
    >
      <mat-icon class="text-primary mb-1">close</mat-icon>
    </button>
  </div>

  <div class="h-full flex flex-col items-center justify-center gap-3">
    <img src="assets/icons/cloud_success.svg" class="mb-10" />
    <div class="text-[34px] font-bold">
      {{ 'settings.upload.titleSuccess' | translate }}
    </div>
    <div class="text-lg text-neutral">
      {{ 'settings.upload.descSuccess' | translate }}
    </div>
  </div>
  <div class="flex gap-2 justify-end">
    <xemex-outlined-button class="float-right" (click)="resetStatus(); dialogRef.close()">
      {{ 'general.btnClose' | translate }}
    </xemex-outlined-button>
  </div>
</ng-template>
<ng-template #Failed>
  <div class="flex justify-end items-center">
    <button
      mat-icon-button
      class="float-right -mt-3 -mr-3"
      (click)="resetStatus(); dialogRef.close()"
    >
      <mat-icon class="text-primary mb-1">close</mat-icon>
    </button>
  </div>

  <div class="h-full flex flex-col items-center justify-center gap-3">
    <img src="assets/icons/cloud_fail.svg" class="mb-10" />
    <div class="text-[34px] font-bold">
      {{ 'settings.upload.titleFailed' | translate }}
    </div>
    <div class="text-lg text-neutral">
      {{ 'settings.upload.descFailed' | translate }}
    </div>
    @if (settingsStore.uploadError()) {
      <div class="text-sm text-red-500">{{ settingsStore.uploadError() }}</div>
    }
    <div class="flex gap-2 justify-end">
      <xemex-outlined-button class="float-right" (click)="resetStatus()">
        {{ 'general.btnTryAgain' | translate }}
      </xemex-outlined-button>
    </div>
  </div>
</ng-template>
<ng-template #Uploading>
  <div class="flex justify-between items-center">
    <div class="text-2xl font-semibold">
      {{ 'general.fileUpload' | translate }}
    </div>
    <button
      mat-icon-button
      class="float-right -mt-3 -mr-3"
      (click)="resetStatus(); dialogRef.close()"
    >
      <mat-icon class="text-primary mb-1">close</mat-icon>
    </button>
  </div>
  <div class="h-full">
    <h2 class="text-[18px] font-bold">
      {{ 'general.selectFile' | translate }}
    </h2>
    <div class="flex gap-5 mt-4">
      <div
        xemexFilesDrop
        [acceptedTypes]="acceptedTypes"
        (dropped)="onUploadFile($event)"
        class="w-full border-2 border-dashed rounded-3xl flex flex-col items-center pt-6 pb-8 transition-colors"
        [class.border-primary]="!fileUpload"
        [class.border-green-500]="fileUpload"
      >
        @if (fileUpload) {
          <mat-icon class="text-green-500 text-[80px] !h-[80px] !w-[80px] mb-3"
            >check_circle</mat-icon
          >
          <div class="text-lg font-semibold text-green-600 mb-1">{{ fileUpload.name }}</div>
          <div class="text-sm text-neutral mb-3">{{ (fileUpload.size / 1024).toFixed(1) }} KB</div>
          <xemex-text-button type="button" (click)="fileSelect.click()">
            {{ 'general.btnChange' | translate }}
          </xemex-text-button>
        } @else {
          <mat-icon class="text-primary text-[124px] !h-[124px] !w-[124px] mb-3"
            >cloud_upload</mat-icon
          >
          <div class="text-xl text-primary mb-2">
            {{ 'creation.step1.dragAndDrop' | translate }}
          </div>
          <div class="text-xl text-primary">
            {{ 'creation.step1.or' | translate }}
          </div>
          <xemex-text-button type="button" (click)="fileSelect.click()">
            {{ 'creation.step1.browserUpload' | translate }}
          </xemex-text-button>
        }
        <input
          type="file"
          autocomplete="off"
          #fileSelect
          [hidden]="true"
          [accept]="acceptedTypes"
          xemexBrowseFile
          (fileSelected)="onUploadFile($event)"
          multiple
        />
      </div>
    </div>
  </div>

  @if (existingFile) {
    <div class="bg-amber-50 border border-amber-300 rounded-lg p-4">
      <div class="text-amber-800 font-semibold mb-1">
        {{ 'settings.upload.existingFileWarning' | translate }}
      </div>
      <div class="text-sm text-amber-700">
        {{ 'settings.upload.uploadedBy' | translate }}: {{ existingFile.createdBy }} &bull;
        {{ existingFile.createdOn | date: 'short' }}
        @if (existingFile.isDeleted) {
          ({{ 'general.deleted' | translate }})
        }
      </div>
      <mat-checkbox [(ngModel)]="allowOverride" class="mt-2">
        {{ 'settings.upload.overrideExisting' | translate }}
      </mat-checkbox>
    </div>
  }

  <div class="grid grid-cols-2 gap-4">
    <xemex-partner-select
      [multiple]="true"
      [formControl]="partnersControl"
      [label]="'general.partnerOrPartners' | translate"
      [placeholder]="'general.placeholderInput' | translate"
    />

    <mat-form-field>
      <mat-label class="text-body text-neutral">{{ 'device.deviceType' | translate }}</mat-label>
      <mat-select [(ngModel)]="selectedDeviceTypeId">
        @for (item of deviceTypes; track item) {
          <mat-option [value]="item.deviceTypeId">
            {{ item.description }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field>
      <mat-label class="text-body text-neutral">{{
        'device.firmwareVersions' | translate
      }}</mat-label>
      <select matNativeControl disabled></select>
    </mat-form-field>
  </div>

  <div class="flex gap-2 justify-end">
    <xemex-outlined-button class="float-right" (click)="dialogRef.close()">
      {{ 'general.btnCancel' | translate }}
    </xemex-outlined-button>
    <xemex-outlined-button
      [disabled]="isUploading || isCheckingFile || (!!existingFile && !allowOverride)"
      (click)="onUploadCF()"
    >
      <div class="flex">
        <span class="mr-2">{{ 'general.btnUpload' | translate }}</span>
        @if (isUploading) {
          <xemex-spinner size="sm"></xemex-spinner>
        }
      </div>
    </xemex-outlined-button>
  </div>
</ng-template>
